import api from '@state/api.js';
import timestring from 'timestring';

export default {
  namespaced: true,
  state: {
    currentProject: null,
    games: null,
  },
  mutations: {
    SET_CURRENT_PROJECT(state, newValue) {
      state.currentProject = newValue;
    },
    SET_GAMES(state, newValue) {
      state.games = newValue;
    },
  },
  getters: {
    getGroupedGames(state, getters, rootState, rootGetters) {
      const progressions = getters['getAllProgressions'];
      if (progressions != null) {
        const index = rootGetters['progression/getProgressionIndex'];
        if (index < 0) {
          return [];
        }   
        return progressions[index].tasks.map(({_id}) => state.games.get(_id));
      } else {
        return state.games;
      }
    },
    getGames(state, getters, rootState, rootGetters) {
      if (!state.games) {
        return [];
      }

      let games = [...state.games.values()];
      const progressions = getters['getAllProgressions'];
      if (progressions != null) {
        const index = rootGetters['progression/getProgressionIndex'];
        const progression = progressions[index];
        games = progression.tasks.map(({_id}) => state.games.get(_id));
      }

      const all = games.flatMap(game => {
        return game.tasks ? game.tasks : game;
      });

      return new Map(all.map(i => [i._id, i]));
    },
    getProjectId(state) {
      return state.currentProject && state.currentProject._id;
    },
    getProjectCode(state) {
      return state.currentProject && state.currentProject.code;
    },
    getMaxBadges(state, getters) {
      if (!state.games) {
        return 0;
      }

      const sum = [...getters.getGames.values()].reduce((p, { model: { points } }) => {
        const badgePoints = points?.badgePoints ?? 0;
        return p + badgePoints;
      }, 0);

      return Math.trunc(sum);
    },
    getAllProgressions(state) {
      if (!state.currentProject) { 
        return null;
      }
      if (!state.currentProject.progressionsEnabled) {
        return null;
      }
      return state.currentProject.progressions;
    },
    getNumberOfProgressions(state, getters) {
      const progressions = getters['getAllProgressions'];
      if (progressions != null) {
        return progressions.length;
      }
      return 0;
    },
    getProgressionData: (state, getters) => (index) => {
      const progressions = getters['getAllProgressions'];
      if (progressions != null) {
        return progressions[index];
      }
      return null;
    },
    getTotalProgressionTasks: (state, getters) => {
      const progressions = getters['getAllProgressions'];
      if (!progressions) {
        return 0;
      }

      return progressions.reduce((sum, {tasks}) => sum + tasks.length, 0);
    },
    getProgressionProgress: (state, getters) => (progressionIndex, taskIndex) => {
      const total = getters['getTotalProgressionTasks'];
      const progressions = getters['getAllProgressions'];
      if (!total || !progressions) {
        return 0;
      }

      return progressions.slice(0, progressionIndex + 1).reduce((sum, {tasks}, idx) => idx < taskIndex ? sum + tasks.length : sum + taskIndex, 0) / total;
    },
    getAllProgressionTasks: (state, getters) => {
      const progressions = getters['getAllProgressions'];
      if (!progressions) {
        return [];
      }

      return progressions.reduce((allTasks, {tasks}) => allTasks.concat(tasks), []);
    },
    getTotalDays(state) {
      return Number(state.currentProject && state.currentProject.maxProjectDays);
    },
    hasProgressionsEnabled: state => state.currentProject ? state.currentProject.progressionsEnabled ?? false : false
  },
  actions: {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    async init({dispatch }) {
      await dispatch('fetchProject');
    },

    async fetchProject({commit, rootGetters }) {
      let projects;

      const userId = rootGetters['auth/userId'];
      if (userId) {
        try {
          projects = await api.get(`/api/project`).json();
        } catch (e) {
          projects = [];
        }
        const { games: gamesList = [], ...project } = (projects && projects[0]) || {};

        const games = new Map(gamesList.map(i => [i._id, i]));
        commit('SET_GAMES', games);
        commit('SET_CURRENT_PROJECT', project);

        return project;
      }
    },
  },
};

// ===
// Private helpers
// ===

const fields = [
  ['session.minDuration', 'min'],
  ['sentence.feedback', 'ms'],
  ['sentence.pause', 'ms'],
  'sentence.timeout',
  ['sentence.cue', 'ms'],
  ['sentence.firstCue', 'ms'],
  ['phoneme.feedback', 'ms'],
  ['phoneme.pause', 'ms'],
  'phoneme.timeout',
  ['phoneme.ISI', 'ms'],
];
// eslint-disable-next-line no-unused-vars
function fixupDurations(project) {
  fields.forEach(item => {
    let units = 's';
    if (Array.isArray(item)) {
      [item, units] = item;
    }
    item.split(/[.[\]]+/).reduce((prev, key, index, array) => {
      if (array.length === index + 1) {
        prev[key] = getDuration(prev[key], units);
      }

      return prev[key];
    }, project);
  });
}

function getDuration(value, units) {
  value = String(value);
  const groups = value
    .toLowerCase()
    .replace(/[^.\w+-]+/g, '')
    .match(/[-+]?[0-9.]+[a-z]+/g);
  if (!groups) {
    value = `${value}${units}`;
  }

  return timestring(value, 'ms');
}

// function getSavedState(key) {
//   return JSON.parse(window.localStorage.getItem(key));
// }

// function saveState(key, state) {
//   window.localStorage.setItem(key, JSON.stringify(state));
// }
